import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchQuestions, incrementQuestionViews, updateQuestion } from "../../store/questionsSlice";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Select,
  MenuItem,
  FormControl,
  TextField, Button, Tooltip, InputAdornment, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {Autocomplete} from "@mui/material";
import { TECHNOLOGY_SKILL_MAP } from "../../data/technologySkills";
import "./HomeContent.css";
import { useMsal } from "@azure/msal-react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {getEmptyStateMessage } from "./Utils/questionSortUtils";
export const getPostedTime = (createdAt) => {
  const createdDate = new Date(createdAt);
  const now = new Date();

  const diffMs = now - createdDate;

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) {
    return "Few seconds ago";
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  }

  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? "s" : ""} ago`;
};

const QA = ({ refreshQuestions }) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list: questions, loading } = useSelector(
    (state) => state.questions
  );
  const { searchResults, searchText, loading: searchLoading} = useSelector(
  (state) => state.search
);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [skillFilter, setSkillFilter] = useState("");
  const { accounts } = useMsal();
  const loggedInUserId = accounts?.[0]?.idTokenClaims?.oid;
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editedBody, setEditedBody] = useState("");
  const [sortBy, setSortBy] = useState("");
  const allSkills = Object.values(TECHNOLOGY_SKILL_MAP).flat();
  const [open, setOpen] = useState(false);
 
  useEffect(() => {
  if (searchText.trim()) {
    setPage(1);
    return;
  }

    const delay = setTimeout(() => {
      dispatch(
        fetchQuestions({
          type: sortBy || null,
          skill: skillFilter || null,
        })
      );
      setPage(1);
    }, 400);

    return () => clearTimeout(delay);
  }, [sortBy, skillFilter, refreshQuestions, dispatch, searchText]);
  const totalQuestionsCount = questions.length;
  const matchedResultsCount = searchResults.length;
  const displayedQuestions = searchText.trim()
    ? searchResults
    : questions;

  const processedQuestions = displayedQuestions || [];

  const totalPages = Math.max(
    1,
    Math.ceil(processedQuestions.length / rowsPerPage)
  );

  const currentPage = Math.min(page, totalPages);

  const paginatedQuestions = processedQuestions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

    
  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ fontSize: "20px" }}>
            All Queries
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                color: "#9F9F9D",
              }}
            >
              Total Questions: {totalQuestionsCount}
            </Typography>

            {searchText.trim() && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#000",
                }}
              >
                {matchedResultsCount} result
                {matchedResultsCount !== 1 ? "s" : ""} found
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl size="small" className="custom-select">
            <Autocomplete
              size="small"
              freeSolo
              options={allSkills}
              value={skillFilter}
              open={open}
              onOpen={() => {
                if (skillFilter.length > 0) setOpen(true);
              }}
              onClose={() => setOpen(false)}
              onInputChange={(e, newValue, reason) => {
                setSkillFilter(newValue);

                if (reason === "input") {
                  setOpen(newValue.length > 0);
                }
                if (reason === "clear") {
                  setOpen(false);
                }
              }}
              onChange={(event, newValue) => {
                setSkillFilter(newValue || "");
                setOpen(false);
              }}
              ListboxProps={{
                sx: {
                  "& .MuiAutocomplete-option": {
                    fontSize: "14px", 
                  },
                },
              }}
              sx={{
                width: 250,
                "& .MuiOutlinedInput-root": {
                  height: 35,
                  borderRadius: 0,
                  fontSize: "14px",
                  paddingRight: "6px !important",
                },
                "& .MuiInputBase-input": {
                  padding: "10px 10px",
                  fontSize: "14px",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filter by skill"
                  onBlur={() => setOpen(false)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSkillFilter("");
                            setOpen(false);
                          }}
                          sx={{ p: 0.1, marginRight: "0px" }}
                        >
                          {skillFilter ? (
                            <CloseIcon fontSize="small" sx={{color:"black"}}/>
                          ) : (
                            <SearchIcon fontSize="small" className="search_filter"/>
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl size="small" className={`custom-select sort-select ${sortBy ? "has-value" : ""}`} sx={{ position: "relative" }}>
            <Select
              displayEmpty
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
              //IconComponent={ArrowDropDownIcon}
              MenuProps={{
                    PaperProps: {
                      sx: {
                        "& .MuiMenuItem-root": {
                          fontSize: "14px",
                        },
                      },
                    },
                  }}
              sx={{
                minWidth: 180,
                "& .MuiSelect-icon": {
                  opacity: sortBy ? 0 : 1,
                },
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: "#999" }}>Sort by</span>;
                }
                return selected.charAt(0).toUpperCase() + selected.slice(1);
              }}
            >
              <MenuItem value="" disabled>
                Sort by
              </MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="unanswered">Unanswered</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
            {sortBy && (
              <Box
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSortBy("");
                  setPage(1);
                }}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CloseIcon fontSize="small" />
              </Box>
            )}
          </FormControl>
        </Box>
      </Box>
      {loading || searchLoading ? (
        <Box
          sx={{
            textAlign: "center",
            mt: 6,
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ color: "#000" }}>
            {searchText.trim() ? "Searching..." : "Loading..."}
          </Typography>
        </Box>
      ) : processedQuestions.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            mt: 6,
            p: 4,
            border: "1px dashed #B980DF",
            backgroundColor: "#faf5ff"
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#7500C0", fontWeight: 600, mb: 1 }}
          >
            {getEmptyStateMessage(sortBy)}
          </Typography>
          <Typography variant="body2" color="#777">
            Please check back later or explore other filters.
          </Typography>
        </Box>
      ) : (
        paginatedQuestions.map((q) => (
          <Box className="qa_box"
            key={q.id}
            sx={{
              border: "1px solid #B980DF",
              p: 2,
              paddingLeft: 0,
              mb: 2,
              display: "flex",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 6,
                backgroundColor: "#7500C0",
                height: 40,
              }}
            />

            <Box sx={{ flex: 1 , minWidth: 0}}>
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                <Avatar
                  sx={{ width: 36, height: 36, mr: 1.5 }}
                />

                <Box>
                  <Typography fontWeight={600} sx={{ lineHeight: 1.2 }}>
                    {q.username}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="#818180"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {q.designation}
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  color="#757575"
                  sx={{ ml: "auto" }}
                >
                  Posted {getPostedTime(q.created_at)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>

                {editingQuestionId === q.id ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={editedBody}
                    onChange={(e) => setEditedBody(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        fontSize: "14px",
                        color: "#6B6B6B"
                      },
                    }}
                  />
                ) : (
                  <Typography
                    className="question_typo"
                    onClick={() => {
                      dispatch(incrementQuestionViews(q.id));
                      navigate(`/queries/${q.id}`);
                    }}
                    sx={{
                      color: "#7500C0",
                      fontWeight: 600,
                      mb: 1,
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    Q: {q.body}
                  </Typography>
                )}

                {/* EDIT ICON */}
                {loggedInUserId === q.user_id && editingQuestionId !== q.id && (
                  <EditOutlinedIcon
                    sx={{ fontSize: 18, cursor: "pointer", color: "#7500C0" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingQuestionId(q.id);
                      setEditedBody(q.body);
                    }}
                  />
                )}
              </Box>
              {editingQuestionId === q.id && (
                <Box sx={{ mt: 1, display: "flex", gap: 1, mb: 1 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      dispatch(
                        updateQuestion({
                          questionId: q.id,
                          body: editedBody,
                        })
                      );
                      setEditingQuestionId(null);
                    }}
                    sx={{
                      backgroundColor: "#000",
                      color: "#fff",
                      borderRadius: 0,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#333",
                      },
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setEditingQuestionId(null)}
                    sx={{
                      backgroundColor: "#fff",
                      color: "#000",
                      border: "1px solid #CFCFCF",
                      borderRadius: 0,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    Cancel
                  </Button>

                </Box>
              )}

              <Typography
                variant="body2"
                color="#6B6B6B"
                mb={2}
                sx={{ fontSize: "14px",
                  overflowWrap: "anywhere",
                  wordBreak: "normal" ,
                  minWidth: 0}}
              >

                {q.answers_count > 0 ? (
                  <>
                    <span style={{ marginRight: 4 }}>A:</span>
                    {q.latest_answer ?? "View answers"}
                  </>
                ) : (
                  "No answers yet"
                )}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {/* <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Chip label={q.technology} size="small" className="chipsbox" />
                  <Chip label={q.skill} size="small" className="chipsbox" />
                </Box> */}
                {q.tags && q.tags.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    {q.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" className="chipsbox" />
                    ))}
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Typography variant="body2" color="#7500C0" fontWeight={600}>
                    {q.answers_count}
                    <span
                      style={{
                        color: "#9D9D9B",
                        fontWeight: "500",
                        paddingLeft: "5px",
                      }}
                    >
                      answers
                    </span>
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}

                  >
                    <img
                      src={process.env.PUBLIC_URL + "/images/Home/dot_img.png"}
                      alt="dot"

                    />
                  </Box>
                  <Tooltip title="Views" arrow
                    classes={{
                      tooltip: "custom-tooltip",
                      arrow: "custom-tooltip-arrow",
                    }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component="img"
                        src={process.env.PUBLIC_URL + "/images/Home/view.png"}
                        alt="views"
                        sx={{
                          width: 16,
                          height: 16,
                          transition: "0.2s ease",
                          "&:hover": {
                            content: `url(${process.env.PUBLIC_URL + "/images/Home/Visible.png"})`,
                          },
                        }}
                      />
                      <Typography variant="body2" color="#9D9D9B">
                        {q.views}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>
        ))
      )}
      {/* PAGINATION */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "8px",
          mt: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* LEFT ARROW */}
          <Box
            onClick={() => page > 1 && setPage(page - 1)}
            sx={{
              cursor: page > 1 ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={
                page > 1
                  ? process.env.PUBLIC_URL + "/images/Home/leftenabledArrow.png"
                  : process.env.PUBLIC_URL + "/images/Home/leftdisabledArrow.png"
              }
              alt="Previous"
              style={{ width: 32, height: 32 }}
            />
          </Box>

          {/* PAGE NUMBERS */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {[...Array(totalPages)].slice(0, 5).map((_, i) => (
              <Typography
                key={i}
                onClick={() => setPage(i + 1)}
                sx={{
                  cursor: "pointer",
                  fontWeight: page === i + 1 ? 600 : 400,
                  color: page === i + 1 ? "#7500C0" : "#000",
                  borderBottom:
                    page === i + 1 ? "2px solid #7500C0" : "none",
                  px: 1,
                }}
              >
                {i + 1}
              </Typography>
            ))}

            {totalPages > 5 && (
              <>
                <Typography>...</Typography>
                <Typography
                  sx={{ cursor: "pointer" }}
                  onClick={() => setPage(totalPages)}
                >
                  {totalPages}
                </Typography>
              </>
            )}
          </Box>

          {/* RIGHT ARROW */}
          <Box
            onClick={() => page < totalPages && setPage(page + 1)}
            sx={{
              cursor: page < totalPages ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={
                page < totalPages
                  ? process.env.PUBLIC_URL + "/images/Home/rightenabledArrow.png"
                  : process.env.PUBLIC_URL + "/images/Home/rightdisabledArrow.png"
              }
              alt="Next"
              style={{ width: 32, height: 32 }}
            />
          </Box>
        </Box>

        {/* ITEMS PER PAGE */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "14px" }}>
            Items per page
          </Typography>

          <FormControl size="small">
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(e.target.value);
                setPage(1);
              }}
              sx={{
                width: 70,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "0px",
                },
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default QA;